// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/users/UserTable.jsx

import React from "react";
import { Button } from "../../../design-system/components";
import UserStatusBadge from "./UserStatusBadge";

function formatDate(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString();
}

function roleLabel(user) {
  const assigned = Array.isArray(user?.assigned_roles) ? user.assigned_roles : [];

  if (assigned.length) return assigned.join(", ");
  if (user?.status === "removed") return "No active authority";
  return user?.legacy_role || "No role assigned";
}

export default function UserTable({
  users = [],
  selectedUserId,
  onSelectUser,
  loadingUserId,
  showRemoved = false,
  removedLoading = false,
  onToggleRemoved,
}) {
  return (
    <section className="enterprise-admin-user-directory">
      <div className="enterprise-admin-user-directory-toolbar">
        <div>
          <p className="enterprise-admin-eyebrow">Member directory</p>
          <h3>Organization members</h3>
          <span>{users.length} member{users.length === 1 ? "" : "s"} shown</span>
        </div>

        <Button
          type="button"
          variant="secondary"
          size="small"
          className={`enterprise-admin-removed-toggle${showRemoved ? " active" : ""}`}
          aria-pressed={showRemoved}
          disabled={removedLoading}
          onClick={onToggleRemoved}
        >
          {removedLoading
            ? "Updating…"
            : showRemoved
              ? "Hide removed members"
              : "Show removed members"}
        </Button>
      </div>

      {!users.length ? (
        <div className="enterprise-admin-empty">
          <strong>No organization members found</strong>
          <p>Invite the people who need governed access to OASYS.</p>
        </div>
      ) : (
        <div className="enterprise-admin-table enterprise-admin-user-table">
          <div className="enterprise-admin-table-row enterprise-admin-table-head" aria-hidden="true">
            <span>Member</span>
            <span>Status</span>
            <span>Authority</span>
            <span>Last login</span>
          </div>

          {users.map((user) => {
            const selected = user.user_id === selectedUserId;
            const removed = user.status === "removed";

            return (
              <button
                type="button"
                key={user.user_id}
                className={`enterprise-admin-table-row${selected ? " active" : ""}${removed ? " is-removed" : ""}`}
                onClick={() => onSelectUser?.(user)}
                aria-pressed={selected}
              >
                <span className="enterprise-admin-table-primary">
                  <strong>{user.display_name || user.email || "Unnamed member"}</strong>
                  <small>{user.email || user.user_id}</small>
                  {loadingUserId === user.user_id ? <small>Loading member details…</small> : null}
                </span>

                <span><UserStatusBadge status={user.status} /></span>
                <span className="enterprise-admin-table-value">{roleLabel(user)}</span>
                <span className="enterprise-admin-table-value">{formatDate(user.last_login_at)}</span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
